// DashboardHome.js
import React, { useState, useEffect } from 'react';
import apiClient from '../apis/apiClient';
import {
  FiCalendar,
  FiShoppingCart,
  FiXCircle,
  FiDollarSign,
  FiUser,
  FiTrendingUp,
  FiClock,
  FiPieChart,
  FiList
} from 'react-icons/fi';
import './DashboardHome.css';

// Try to import chart libraries with fallback
let Bar;
let ChartJS;

try {
  const chartJS = require('chart.js');
  const reactChartJS = require('react-chartjs-2');

  Bar = reactChartJS.Bar;
  ChartJS = chartJS.Chart;

  const {
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
  } = chartJS;

  ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
  );
} catch (e) {
  console.warn("Chart libraries not available. Charts will be disabled.", e);
}

const DashboardHome = () => {
  const [metrics, setMetrics] = useState({
    totalOrders: 0,
    totalReservations: 0,
    cancellations: 0,
    revenue: 0,
    activeUsers: 0,
    // Order status breakdown
    completedOrders: 0,
    processingOrders: 0,
    shippedOrders: 0,
    pendingOrders: 0,
    // Reservation status breakdown
    confirmedReservations: 0,
    pendingReservations: 0,
    canceledReservations: 0
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartsAvailable, setChartsAvailable] = useState(false);

  // Check if charts are available
  useEffect(() => {
    setChartsAvailable(!!Bar && !!ChartJS);
  }, []);

  // Fetch all dashboard data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch orders data
        const ordersResponse = await apiClient.get('/order');
        const ordersData = ordersResponse.data;

        // Fetch cancellations data
        const cancellationsResponse = await apiClient.get('/cancellations');
        const cancellationsData = cancellationsResponse.data || [];

        // Fetch reservations data
        const reservationsResponse = await apiClient.get('/reservations');
        const reservationsData = reservationsResponse.data;

        // Calculate order metrics
        const completedOrders = ordersData.filter(o => o.status === 'DELIVERED').length;
        const processingOrders = ordersData.filter(o => o.status === 'PROCESSING').length;
        const shippedOrders = ordersData.filter(o => o.status === 'SHIPPED').length;
        const pendingOrders = ordersData.filter(o => o.status === 'PENDING' || o.status === 'PLACED').length;
        const totalRevenue = ordersData.reduce((sum, order) => sum + (order.totalAmount || 0), 0);

        // Calculate reservation metrics
        const confirmedReservations = reservationsData.filter(r => r.status === 'CONFIRMED').length;
        const pendingReservations = reservationsData.filter(r => r.status === 'PENDING').length;
        const canceledReservations = reservationsData.filter(r => r.status === 'CANCELLED').length;

        // Fetch active users count dynamically
        let activeUsersCount = 0;
        try {
          const usersResponse = await apiClient.get('/users/count');
          activeUsersCount = typeof usersResponse.data === 'number' ? usersResponse.data : (parseInt(usersResponse.data) || 0);
        } catch (uErr) {
          console.warn('Could not fetch user count:', uErr);
        }

        setMetrics({
          totalOrders: ordersData.length,
          totalReservations: reservationsData.length,
          cancellations: cancellationsData.length,
          revenue: totalRevenue,
          activeUsers: activeUsersCount,
          completedOrders,
          processingOrders,
          shippedOrders,
          pendingOrders,
          confirmedReservations,
          pendingReservations,
          canceledReservations
        });

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Chart data
  const chartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Reservations',
        data: [12, 19, 15, 22, 18, 25, 20],
        backgroundColor: 'rgba(86, 128, 233, 0.7)',
      },
      {
        label: 'Orders',
        data: [18, 15, 21, 19, 24, 22, 27],
        backgroundColor: 'rgba(103, 214, 157, 0.7)',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Weekly Performance',
        font: {
          size: 16
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 5
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="dashboard-home loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-home error">
        <div className="error-message">
          Error: {error}
          <button onClick={() => setError(null)}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-home">
      <div className="dashboard-header">
        <h1>Dashboard-Übersicht</h1>
        <p>Willkommen zurück, Mooty! Hier ist, was heute passiert.</p>
      </div>

      {/* Metrics Cards */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon" style={{ backgroundColor: 'rgba(86, 128, 233, 0.1)' }}>
            <FiShoppingCart size={24} color="#5680e9" />
          </div>
          <div className="metric-info">
            <h3>Gesamtbestellungen</h3>
            <p>{metrics.totalOrders}</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon" style={{ backgroundColor: 'rgba(103, 214, 157, 0.1)' }}>
            <FiCalendar size={24} color="#67d69d" />
          </div>
          <div className="metric-info">
            <h3>Gesamtzahl der Reservierungen</h3>
            <p>{metrics.totalReservations}</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon" style={{ backgroundColor: 'rgba(255, 99, 132, 0.1)' }}>
            <FiXCircle size={24} color="#ff6384" />
          </div>
          <div className="metric-info">
            <h3>Stornierungen</h3>
            <p>{metrics.cancellations}</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon" style={{ backgroundColor: 'rgba(255, 206, 86, 0.1)' }}>
            <FiDollarSign size={24} color="#ffce56" />
          </div>
          <div className="metric-info">
            <h3>Gesamtumsatz</h3>
            <p>${metrics.revenue.toFixed(2)}</p>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon" style={{ backgroundColor: 'rgba(75, 192, 192, 0.1)' }}>
            <FiUser size={24} color="#4bc0c0" />
          </div>
          <div className="metric-info">
            <h3>Aktive Benutzer</h3>
            <p>{metrics.activeUsers}</p>
          </div>
        </div>

        {/* <div className="metric-card">
          <div className="metric-icon" style={{ backgroundColor: 'rgba(153, 102, 255, 0.1)' }}>
            <FiTrendingUp size={24} color="#9966ff" />
          </div>
          <div className="metric-info">
            <h3>Growth Rate</h3>
            <p>+12.5%</p>
          </div>
        </div> */}
      </div>

      {/* Summary Sections */}
      <div className="summary-section">
        <div className="summary-card">
          <div className="summary-header">
            <FiShoppingCart className="summary-icon" />
            <h3>Bestellübersicht</h3>
          </div>
          <div className="summary-stats">
            <div className="summary-stat">
              <span className="stat-value">{metrics.completedOrders}</span>
              <span className="stat-label">Vollendet</span>
            </div>
            <div className="summary-stat">
              <span className="stat-value">{metrics.processingOrders}</span>
              <span className="stat-label">Verarbeitung</span>
            </div>
            <div className="summary-stat">
              <span className="stat-value">{metrics.shippedOrders}</span>
              <span className="stat-label">Ausgeliefert</span>
            </div>
            <div className="summary-stat">
              <span className="stat-value">{metrics.pendingOrders}</span>
              <span className="stat-label">Ausstehend</span>
            </div>
          </div>
        </div>

        {/* <div className="summary-card">
          <div className="summary-header">
            <FiCalendar className="summary-icon" />
            <h3>Reservations Summary</h3>
          </div>
          <div className="summary-stats">
            <div className="summary-stat">
              <span className="stat-value">{metrics.confirmedReservations}</span>
              <span className="stat-label">Confirmed</span>
            </div>
            <div className="summary-stat">
              <span className="stat-value">{metrics.pendingReservations}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="summary-stat">
              <span className="stat-value">{metrics.canceledReservations}</span>
              <span className="stat-label">Canceled</span>
            </div>
          </div>
        </div> */}
      </div>

      {/* Charts */}
      <div className="charts-container">
        {/* <div className="chart-card">
          {chartsAvailable ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <div className="chart-fallback">
              <h3>Chart Unavailable</h3>
              <p>Install chart dependencies to view analytics:</p>
              <pre>npm install react-chartjs-2 chart.js</pre>
              <div className="chart-placeholder">
                <div className="placeholder-content">
                  <div className="placeholder-bar" style={{ height: '70%' }}></div>
                  <div className="placeholder-bar" style={{ height: '90%' }}></div>
                  <div className="placeholder-bar" style={{ height: '60%' }}></div>
                  <div className="placeholder-bar" style={{ height: '80%' }}></div>
                  <div className="placeholder-bar" style={{ height: '65%' }}></div>
                  <div className="placeholder-bar" style={{ height: '75%' }}></div>
                  <div className="placeholder-bar" style={{ height: '85%' }}></div>
                </div>
              </div>
            </div>
          )}
        </div> */}
        {/*         
        <div className="stats-card">
          <h3>Performance Summary</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <FiTrendingUp size={20} />
              <div>
                <p>Order Conversion</p>
                <h4>78.5%</h4>
              </div>
            </div>
            <div className="stat-item">
              <FiClock size={20} />
              <div>
                <p>Avg. Processing Time</p>
                <h4>2.3 days</h4>
              </div>
            </div>
            <div className="stat-item">
              <FiUser size={20} />
              <div>
                <p>New Customers</p>
                <h4>24</h4>
              </div>
            </div>
            <div className="stat-item">
              <FiPieChart size={20} />
              <div>
                <p>Reservation Rate</p>
                <h4>65%</h4>
              </div>
            </div>
          </div>
        </div> */}
      </div>

      {/* Recent Activity */}
      {/* <div className="recent-activity">
        <h2>Recent Activity</h2>
        <ul className="activity-list">
          <li>
            <div className="activity-icon">
              <FiShoppingCart />
            </div>
            <div className="activity-content">
              <p><strong>New order</strong> #ORD-{Math.floor(Math.random() * 10000)} placed</p>
              <span className="activity-time">2 minutes ago</span>
            </div>
          </li>
          <li>
            <div className="activity-icon">
              <FiCalendar />
            </div>
            <div className="activity-content">
              <p><strong>New reservation</strong> by customer #{Math.floor(Math.random() * 1000)}</p>
              <span className="activity-time">15 minutes ago</span>
            </div>
          </li>
          <li>
            <div className="activity-icon">
              <FiUser />
            </div>
            <div className="activity-content">
              <p><strong>New user</strong> registered</p>
              <span className="activity-time">1 hour ago</span>
            </div>
          </li>
          <li>
        
          </li>
        </ul>
      </div> */}
    </div>
  );
};

export default DashboardHome;